import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../authentication_components/authContext';
import { useBusinessData } from '../../authentication_components/businessDataContext';
import { useBusinessChangeLogs } from '../../authentication_components/businessChangeContext';
import { useEmailChange } from '../../authentication_components/emailChangeContext';
import { useBusinessDeletion } from '../../authentication_components/businessDeletionContext';
import { useBusinessVerification } from '../../authentication_components/adminVerificationContext';
import { useSMSRelay } from '../../authentication_components/phoneVerificationContext';
import { useBusinessSchedule } from '../../authentication_components/businessScheduleContext';
import { BusinessLocationProvider } from '../../authentication_components/businessLocationContext';
import { BusinessPublicToggleProvider, useBusinessPublicToggle } from '../../authentication_components/businessPublicToggleContext';
import { AdminTrackerProvider, useAdminTracker } from '../../authentication_components/adminTrackerContext';
import { useBusinessEdits } from '../../authentication_components/businessEditContext';
import { useBusinessRegistration } from '../../authentication_components/businessRegistrationContext';
import { useBusinessSocials } from '../../authentication_components/businessSocialsContext';

import BusinessSocialsDialog from './admin_dashboard_subcomponents/business_socials_dialog';
import BusinessChangeLogDialog from '../dialog_components/business_history_logs';
import BusinessEditDialog from '../dialog_components/business_dialog_components'
import SMSVerificationDialog from './admin_dashboard_subcomponents/sms_verification_dialog';
import BusinessLocationDialog from './admin_dashboard_subcomponents/business_location_dialog';
import {
  Search, Calendar, Phone, Mail, Building, User, Loader2, AlertCircle, CheckCircle,
  XCircle, Filter, Download, RefreshCw, Eye, Edit2, Send, X, AlertTriangle, Shield,
  AlertOctagon, Trash2, Zap, Globe, MessageSquare, Settings, Key, Save, FileText,
  PlusCircle, ClipboardList, Clock, MapPin, Link2, Copy
} from 'lucide-react';

const MAX_ALL_BUSINESSES = 500;

// =================================================================
// 0. ReDefaultEvaluationDialog Component
// =================================================================
const ReDefaultEvaluationDialog = ({ isOpen, onClose, business }) => {
  const { reDefaultSubscriptionEvaluation, clearError } = useBusinessRegistration();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();


  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError(null);
      setLoading(false);
      clearError();
    }
  }, [isOpen, clearError]);

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    try {
      await reDefaultSubscriptionEvaluation(business.business_registry_number);

      // Track action
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'subscription_evaluation_reset',
          change_reason: `Business ${business.business_registry_number} - Subscription evaluation limits reset to default Personal tier`
        });
      } catch (e) {
        console.error("Tracking failed", e);
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || "Falló la actualización de la evaluación.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-blue-600" />
            Reiniciar Evaluación
          </h3>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <h4 className="font-semibold text-gray-900">{business.business_name}</h4>
            <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
          </div>

          {!success ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
                <p>
                  Esta acción <strong>creará o reiniciará</strong> la tabla de <em>Subscription Evaluation</em> con los valores por defecto del plan <strong>Personal</strong> (20 productos, 10 servicios, etc.).
                </p>
                <p className="mt-2 font-medium">
                  Útil si el negocio no puede crear productos por falta de registros en la base de datos.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <button
                onClick={handleExecute}
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
                Generar Valores por Defecto
              </button>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8" />
              </div>
              <h5 className="text-lg font-bold text-gray-800 mb-2">¡Actualización Exitosa!</h5>
              <p className="text-sm text-gray-600 mb-6">
                Los límites de suscripción han sido restaurados correctamente.
              </p>
              <button
                onClick={onClose}
                className="w-full py-2 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 0b. BusinessScheduleDialog Component
// =================================================================
const BusinessScheduleDialog = ({ isOpen, onClose, business }) => {
  const {
    getSchedulesByBusiness,
    createDefaultWeekSchedule,
    updateScheduleByBusinessAndDay,
    loading,
    error,
    clearError,
    formatTimeForDisplay,
    DAY_NAMES
  } = useBusinessSchedule();

  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  const [schedules, setSchedules] = useState([]);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [propagatingDay, setPropagatingDay] = useState(null);
  const [propagateTargets, setPropagateTargets] = useState([]);
  const [propagateLoading, setPropagateLoading] = useState(false);

  // Initialize with safe defaults
  const [editForm, setEditForm] = useState({
    openTime: '09:00',
    closeTime: '17:00',
    isClosed: false,
    is24Hours: false
  });

  // Quick Set feature state
  const [quickSetForm, setQuickSetForm] = useState({
    openTime: '09:00',
    closeTime: '17:00',
    isClosed: false,
    is24Hours: false
  });
  const [quickSetTargets, setQuickSetTargets] = useState([]);
  const [quickSetLoading, setQuickSetLoading] = useState(false);
  const [showQuickSet, setShowQuickSet] = useState(false);

  // --- Helper: Robust Time Extractor ---
  // Extracts "HH:MM" from any string (e.g., "09:00:00", "2026-01-01T09:00:00", etc.)
  const extractSafeTime = (input, defaultTime = '09:00') => {
    if (!input) return defaultTime;
    try {
      const str = String(input);
      // Regex to find HH:MM pattern anywhere in the string
      // Matches 00-23 for hours and 00-59 for minutes
      const match = str.match(/([01]?[0-9]|2[0-3]):([0-5][0-9])/);

      if (match) {
        let [full, h, m] = match;
        // Ensure 2-digit hour (e.g., 9:00 -> 09:00) for HTML input compatibility
        if (h.length === 1) h = `0${h}`;
        return `${h}:${m}`;
      }
    } catch (e) {
      console.warn("Time parsing error:", e);
    }
    return defaultTime;
  };

  useEffect(() => {
    if (isOpen && business) {
      setInitialFetchDone(false);
      setSuccessMessage('');
      setLocalError(null);
      setSchedules([]);
      setHasSchedule(false);
      setEditingDay(null);
      clearError();

      const loadSchedules = async () => {
        try {
          const result = await getSchedulesByBusiness(business.business_registry_number);
          if (result && result.schedules && result.schedules.length > 0) {
            setSchedules(result.schedules);
            setHasSchedule(true);
          } else {
            setHasSchedule(false);
          }
        } catch (err) {
          console.error("Failed to fetch schedules:", err);
          if (err.message && (err.message.includes('not found') || err.message.includes('404'))) {
            setHasSchedule(false);
            clearError();
          } else {
            setLocalError(err.message);
          }
        } finally {
          setInitialFetchDone(true);
        }
      };
      loadSchedules();
    }
  }, [isOpen, business, getSchedulesByBusiness, clearError]);

  const handleGenerateSchedule = async () => {
    setLocalError(null);
    setSuccessMessage('');

    try {
      const result = await createDefaultWeekSchedule(business.business_registry_number, adminId || 'system');
      setSchedules(result.schedules || []);
      setHasSchedule(true);
      setSuccessMessage('Horario generado exitosamente (Lun-Vie 9AM-5PM, Sáb-Dom cerrado).');

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'business_schedule_create',
        change_reason: `Business ${business.business_registry_number} - Generated default weekly schedule`
      });

    } catch (err) {
      setLocalError(err.message || 'Error al generar horario');
    }
  };

  const handleEditClick = (schedule) => {
    setEditingDay(schedule.day_of_week);

    // USE THE HELPER: This prevents "0000-" errors by using regex to find the time
    const safeOpen = extractSafeTime(schedule.open_time, '09:00');
    const safeClose = extractSafeTime(schedule.close_time, '17:00');

    setEditForm({
      openTime: safeOpen,
      closeTime: safeClose,
      isClosed: schedule.is_closed,
      is24Hours: schedule.is_24_hours
    });
    setSuccessMessage('');
    setLocalError(null);
  };

  const handleCancelEdit = () => {
    setEditingDay(null);
    setEditForm({ openTime: '09:00', closeTime: '17:00', isClosed: false, is24Hours: false });
  };

  const handleSaveEdit = async () => {
    setLocalError(null);
    try {
      const updateData = {
        isClosed: editForm.isClosed,
        is24Hours: editForm.is24Hours,
        editedBy: adminId || 'system'
      };

      if (!editForm.isClosed && !editForm.is24Hours) {
        if (!editForm.openTime || !editForm.closeTime) {
          setLocalError('Por favor ingrese hora de apertura y cierre');
          return;
        }
        // Send exactly what is in the form state (which is now guaranteed to be HH:MM or valid)
        updateData.openTime = editForm.openTime;
        updateData.closeTime = editForm.closeTime;
      }

      await updateScheduleByBusinessAndDay(business.business_registry_number, editingDay, updateData);

      // Refresh schedules
      const result = await getSchedulesByBusiness(business.business_registry_number);
      if (result && result.schedules) {
        setSchedules(result.schedules);
      }

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'business_schedule_update',
        change_reason: `Business ${business.business_registry_number} - Updated ${DAY_NAMES[editingDay]} schedule`
      });

      setSuccessMessage(`Horario de ${DAY_NAMES[editingDay]} actualizado.`);
      setEditingDay(null);
    } catch (err) {
      setLocalError(err.message || 'Error al actualizar horario');
    }
  };

  const handlePropagateClick = (schedule) => {
    setPropagatingDay(schedule.day_of_week);
    setPropagateTargets([]);
    setSuccessMessage('');
    setLocalError(null);
  };

  const handleCancelPropagate = () => {
    setPropagatingDay(null);
    setPropagateTargets([]);
  };

  const togglePropagateTarget = (dayOfWeek) => {
    setPropagateTargets(prev =>
      prev.includes(dayOfWeek)
        ? prev.filter(d => d !== dayOfWeek)
        : [...prev, dayOfWeek]
    );
  };

  const handleConfirmPropagate = async () => {
    if (propagateTargets.length === 0) {
      setLocalError('Seleccione al menos un día destino.');
      return;
    }

    setPropagateLoading(true);
    setLocalError(null);

    const sourceSchedule = schedules.find(s => s.day_of_week === propagatingDay);
    if (!sourceSchedule) {
      setLocalError('No se encontró el horario origen.');
      setPropagateLoading(false);
      return;
    }

    try {
      for (const targetDay of propagateTargets) {
        const updateData = {
          isClosed: sourceSchedule.is_closed,
          is24Hours: sourceSchedule.is_24_hours,
          editedBy: adminId || 'system'
        };

        if (!sourceSchedule.is_closed && !sourceSchedule.is_24_hours) {
          updateData.openTime = extractSafeTime(sourceSchedule.open_time, '09:00');
          updateData.closeTime = extractSafeTime(sourceSchedule.close_time, '17:00');
        }

        await updateScheduleByBusinessAndDay(business.business_registry_number, targetDay, updateData);
      }

      // Refresh schedules
      const result = await getSchedulesByBusiness(business.business_registry_number);
      if (result && result.schedules) {
        setSchedules(result.schedules);
      }

      const targetDayNames = propagateTargets.map(d => DAY_NAMES[d]).join(', ');
      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'business_schedule_propagate',
        change_reason: `Business ${business.business_registry_number} - Propagated ${DAY_NAMES[propagatingDay]} schedule to: ${targetDayNames}`
      });

      setSuccessMessage(`Horario de ${DAY_NAMES[propagatingDay]} propagado a: ${targetDayNames}`);
      setPropagatingDay(null);
      setPropagateTargets([]);
    } catch (err) {
      setLocalError(err.message || 'Error al propagar horario');
    } finally {
      setPropagateLoading(false);
    }
  };

  // Quick Set handlers
  const toggleQuickSetTarget = (dayOfWeek) => {
    setQuickSetTargets(prev =>
      prev.includes(dayOfWeek)
        ? prev.filter(d => d !== dayOfWeek)
        : [...prev, dayOfWeek]
    );
  };

  const selectAllDays = () => {
    const allDays = schedules.map(s => s.day_of_week);
    setQuickSetTargets(allDays);
  };

  const selectWeekdays = () => {
    // Days 1-5 are typically Monday-Friday
    const weekdays = schedules.filter(s => s.day_of_week >= 1 && s.day_of_week <= 5).map(s => s.day_of_week);
    setQuickSetTargets(weekdays);
  };

  const selectWeekends = () => {
    // Days 0 and 6 are typically Sunday and Saturday
    const weekends = schedules.filter(s => s.day_of_week === 0 || s.day_of_week === 6).map(s => s.day_of_week);
    setQuickSetTargets(weekends);
  };

  const clearQuickSetTargets = () => {
    setQuickSetTargets([]);
  };

  const handleQuickSetApply = async () => {
    if (quickSetTargets.length === 0) {
      setLocalError('Seleccione al menos un día para aplicar el horario.');
      return;
    }

    setQuickSetLoading(true);
    setLocalError(null);

    try {
      for (const targetDay of quickSetTargets) {
        const updateData = {
          isClosed: quickSetForm.isClosed,
          is24Hours: quickSetForm.is24Hours,
          editedBy: adminId || 'system'
        };

        if (!quickSetForm.isClosed && !quickSetForm.is24Hours) {
          updateData.openTime = quickSetForm.openTime;
          updateData.closeTime = quickSetForm.closeTime;
        }

        await updateScheduleByBusinessAndDay(business.business_registry_number, targetDay, updateData);
      }

      // Refresh schedules
      const result = await getSchedulesByBusiness(business.business_registry_number);
      if (result && result.schedules) {
        setSchedules(result.schedules);
      }

      const targetDayNames = quickSetTargets.map(d => DAY_NAMES[d]).join(', ');

      let scheduleDescription = '';
      if (quickSetForm.isClosed) {
        scheduleDescription = 'Cerrado';
      } else if (quickSetForm.is24Hours) {
        scheduleDescription = '24 Horas';
      } else {
        scheduleDescription = `${quickSetForm.openTime} - ${quickSetForm.closeTime}`;
      }

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'business_schedule_quick_set',
        change_reason: `Business ${business.business_registry_number} - Quick set (${scheduleDescription}) applied to: ${targetDayNames}`
      });

      setSuccessMessage(`Horario (${scheduleDescription}) aplicado a: ${targetDayNames}`);
      setQuickSetTargets([]);
      setShowQuickSet(false);
    } catch (err) {
      setLocalError(err.message || 'Error al aplicar horario rápido');
    } finally {
      setQuickSetLoading(false);
    }
  };

  const handleCancelQuickSet = () => {
    setShowQuickSet(false);
    setQuickSetTargets([]);
    setQuickSetForm({
      openTime: '09:00',
      closeTime: '17:00',
      isClosed: false,
      is24Hours: false
    });
  };

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-teal-600" />
            Horario del Negocio
          </h3>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="text-center mb-6">
            <h4 className="font-semibold text-gray-900">{business.business_name}</h4>
            <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
          </div>

          {!initialFetchDone ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 text-teal-500 animate-spin" />
            </div>
          ) : !hasSchedule ? (
            <div className="flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50">
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mb-4 text-gray-400">
                <Clock className="h-8 w-8" />
              </div>
              <h5 className="text-gray-700 font-semibold mb-2">Sin Horario Configurado</h5>
              <p className="text-sm text-center text-gray-500 mb-6 max-w-[300px]">
                Este negocio aún no tiene un horario registrado en la base de datos.
              </p>
              <button
                onClick={handleGenerateSchedule}
                disabled={loading}
                className="py-3 px-6 rounded-xl font-bold text-sm bg-teal-600 text-white hover:bg-teal-700 shadow-sm transition-all flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                Generar Horario por Defecto
              </button>
              <p className="text-xs text-gray-400 mt-3">
                Lun-Vie: 9:00 AM - 5:00 PM | Sáb-Dom: Cerrado
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Quick Set Feature */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden">
                {!showQuickSet ? (
                  <button
                    onClick={() => setShowQuickSet(true)}
                    disabled={editingDay !== null || propagatingDay !== null}
                    className="w-full p-4 flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-50 hover:text-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Zap className="h-5 w-5" />
                    <span className="font-medium">Configuración Rápida de Horario</span>
                  </button>
                ) : (
                  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-semibold text-gray-800 flex items-center gap-2">
                        <Zap className="h-5 w-5 text-teal-600" />
                        Configuración Rápida
                      </h5>
                      <button
                        onClick={handleCancelQuickSet}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Time Configuration */}
                    <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-3">Configurar horario:</p>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Apertura</label>
                          <input
                            type="time"
                            value={quickSetForm.openTime}
                            onChange={(e) => setQuickSetForm({ ...quickSetForm, openTime: e.target.value })}
                            disabled={quickSetForm.isClosed || quickSetForm.is24Hours}
                            className={`w-full p-2 border rounded text-sm ${(quickSetForm.isClosed || quickSetForm.is24Hours) ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Cierre</label>
                          <input
                            type="time"
                            value={quickSetForm.closeTime}
                            onChange={(e) => setQuickSetForm({ ...quickSetForm, closeTime: e.target.value })}
                            disabled={quickSetForm.isClosed || quickSetForm.is24Hours}
                            className={`w-full p-2 border rounded text-sm ${(quickSetForm.isClosed || quickSetForm.is24Hours) ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                          />
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={quickSetForm.isClosed}
                            onChange={(e) => setQuickSetForm({
                              ...quickSetForm,
                              isClosed: e.target.checked,
                              is24Hours: false
                            })}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className={quickSetForm.isClosed ? 'text-red-600 font-medium' : 'text-gray-600'}>Cerrado</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={quickSetForm.is24Hours}
                            onChange={(e) => setQuickSetForm({
                              ...quickSetForm,
                              is24Hours: e.target.checked,
                              isClosed: false
                            })}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className={quickSetForm.is24Hours ? 'text-green-600 font-medium' : 'text-gray-600'}>24 Horas</span>
                        </label>
                      </div>

                      {/* Preview */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">Vista previa:</p>
                        <p className="text-sm font-semibold mt-1">
                          {quickSetForm.isClosed ? (
                            <span className="text-red-600">Cerrado</span>
                          ) : quickSetForm.is24Hours ? (
                            <span className="text-green-600">Abierto 24 Horas</span>
                          ) : (
                            <span className="text-teal-600">{formatTimeForDisplay(quickSetForm.openTime)} - {formatTimeForDisplay(quickSetForm.closeTime)}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Day Selection */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-gray-700">Aplicar a los días:</p>
                        <div className="flex gap-1">
                          <button
                            onClick={selectAllDays}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Todos
                          </button>
                          <button
                            onClick={selectWeekdays}
                            className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                          >
                            L-V
                          </button>
                          <button
                            onClick={selectWeekends}
                            className="px-2 py-1 text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 rounded transition-colors"
                          >
                            S-D
                          </button>
                          <button
                            onClick={clearQuickSetTargets}
                            className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
                          >
                            Ninguno
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {schedules
                          .sort((a, b) => a.day_of_week - b.day_of_week)
                          .map(s => {
                            const isSelected = quickSetTargets.includes(s.day_of_week);
                            return (
                              <button
                                key={s.day_of_week}
                                onClick={() => toggleQuickSetTarget(s.day_of_week)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${isSelected
                                  ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'
                                  }`}
                              >
                                {s.day_name}
                              </button>
                            );
                          })}
                      </div>

                      {quickSetTargets.length > 0 && (
                        <p className="text-xs text-teal-700 mt-3 font-medium">
                          ✓ {quickSetTargets.length} día(s) seleccionado(s)
                        </p>
                      )}
                    </div>

                    {/* Apply Button */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleCancelQuickSet}
                        disabled={quickSetLoading}
                        className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleQuickSetApply}
                        disabled={quickSetLoading || quickSetTargets.length === 0}
                        className="flex-1 py-2.5 px-4 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      >
                        {quickSetLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        Aplicar Horario
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-500 font-medium">Horario por día</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* The Schedule List */}
              <div className="space-y-3">
                {schedules.sort((a, b) => a.day_of_week - b.day_of_week).map((schedule) => (
                  <div
                    key={schedule.id}
                    className={`p-4 rounded-lg border transition-all ${schedule.is_closed
                      ? 'bg-red-50 border-red-200'
                      : schedule.is_24_hours
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-gray-200'
                      }`}
                  >
                    {editingDay === schedule.day_of_week ? (
                      <div className="space-y-3">
                        <div className="font-semibold text-gray-800">{schedule.day_name}</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-600">Apertura</label>
                            <input
                              type="time"
                              value={editForm.openTime}
                              onChange={(e) => setEditForm({ ...editForm, openTime: e.target.value })}
                              disabled={editForm.isClosed || editForm.is24Hours}
                              className={`w-full p-2 border rounded text-sm ${(editForm.isClosed || editForm.is24Hours) ? 'bg-gray-100 text-gray-400' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Cierre</label>
                            <input
                              type="time"
                              value={editForm.closeTime}
                              onChange={(e) => setEditForm({ ...editForm, closeTime: e.target.value })}
                              disabled={editForm.isClosed || editForm.is24Hours}
                              className={`w-full p-2 border rounded text-sm ${(editForm.isClosed || editForm.is24Hours) ? 'bg-gray-100 text-gray-400' : ''}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={editForm.isClosed}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setEditForm({
                                  ...editForm,
                                  isClosed: isChecked,
                                  is24Hours: false,
                                  openTime: editForm.openTime || '09:00',
                                  closeTime: editForm.closeTime || '17:00'
                                });
                              }}
                              className="rounded"
                            />
                            Cerrado
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={editForm.is24Hours}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setEditForm({
                                  ...editForm,
                                  is24Hours: isChecked,
                                  isClosed: false,
                                  openTime: editForm.openTime || '09:00',
                                  closeTime: editForm.closeTime || '17:00'
                                });
                              }}
                              className="rounded"
                            />
                            24 Horas
                          </label>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={loading}
                            className="flex-1 py-2 px-3 bg-teal-600 text-white rounded text-sm font-medium hover:bg-teal-700 flex items-center justify-center gap-1"
                          >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Guardar
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={loading}
                            className="py-2 px-3 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : propagatingDay === schedule.day_of_week ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-gray-800">{schedule.day_name}</div>
                          <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Origen</span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {schedule.is_closed ? (
                            <span className="text-red-600 font-medium">Cerrado</span>
                          ) : schedule.is_24_hours ? (
                            <span className="text-green-600 font-medium">Abierto 24 Horas</span>
                          ) : (
                            <span>{formatTimeForDisplay(extractSafeTime(schedule.open_time))} - {formatTimeForDisplay(extractSafeTime(schedule.close_time))}</span>
                          )}
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-800 mb-2">Seleccione los días destino:</p>
                          <div className="flex flex-wrap gap-2">
                            {schedules
                              .filter(s => s.day_of_week !== propagatingDay)
                              .sort((a, b) => a.day_of_week - b.day_of_week)
                              .map(s => {
                                const isSelected = propagateTargets.includes(s.day_of_week);
                                return (
                                  <button
                                    key={s.day_of_week}
                                    onClick={() => togglePropagateTarget(s.day_of_week)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${isSelected
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                      }`}
                                  >
                                    {s.day_name}
                                  </button>
                                );
                              })}
                          </div>
                          {propagateTargets.length > 0 && (
                            <p className="text-xs text-blue-700 mt-2">
                              {propagateTargets.length} día(s) seleccionado(s)
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={handleConfirmPropagate}
                            disabled={propagateLoading || propagateTargets.length === 0}
                            className="flex-1 py-2 px-3 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-1 disabled:opacity-50"
                          >
                            {propagateLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Propagar
                          </button>
                          <button
                            onClick={handleCancelPropagate}
                            disabled={propagateLoading}
                            className="py-2 px-3 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-800">{schedule.day_name}</div>
                          <div className="text-sm text-gray-600">
                            {schedule.is_closed ? (
                              <span className="text-red-600 font-medium">Cerrado</span>
                            ) : schedule.is_24_hours ? (
                              <span className="text-green-600 font-medium">Abierto 24 Horas</span>
                            ) : (
                              <span>{formatTimeForDisplay(extractSafeTime(schedule.open_time))} - {formatTimeForDisplay(extractSafeTime(schedule.close_time))}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handlePropagateClick(schedule)}
                            disabled={propagatingDay !== null || editingDay !== null}
                            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-30"
                            title="Propagar a otros días"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditClick(schedule)}
                            disabled={propagatingDay !== null}
                            className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-full transition-colors disabled:opacity-30"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(localError || error) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700">{localError || error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 1. PublicToggleDialog Component - COMPLETE VERSION
// =================================================================
const PublicToggleDialog = ({ isOpen, onClose, business }) => {
  const {
    fetchProfileStatus,
    updateProfileField,
    bulkUpdateProfileFields,
    createDefaultProfile,
    loading,
    error,
    clearError
  } = useBusinessPublicToggle();

  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  const [profileFields, setProfileFields] = useState({});
  const [profileExists, setProfileExists] = useState(true);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});

  // Field display configuration
  const fieldConfig = {
    profile_public: { label: 'Perfil Principal', icon: Globe, color: 'blue', requiresImage: true },
    profile_products_public: { label: 'Productos', icon: Building, color: 'purple' },
    profile_services_public: { label: 'Servicios', icon: Settings, color: 'indigo' },
    profile_phone_public: { label: 'Teléfono', icon: Phone, color: 'green' },
    profile_email_public: { label: 'Correo Electrónico', icon: Mail, color: 'green' },
    profile_location_public: { label: 'Ubicación', icon: Building, color: 'orange' },
    profile_schedule_public: { label: 'Horario', icon: Clock, color: 'teal' },
    profile_images_public: { label: 'Galería de Imágenes', icon: Eye, color: 'pink', requiresImage: true },
    profile_employees_public: { label: 'Empleados', icon: User, color: 'cyan' },
    profile_events_public: { label: 'Eventos', icon: Calendar, color: 'yellow' },
    profile_visits_public: { label: 'Estadísticas de Visitas', icon: Eye, color: 'red' }
  };

  useEffect(() => {
    if (isOpen && business) {
      setInitialFetchDone(false);
      setSuccessMessage('');
      setLocalError(null);
      setProfileExists(true);
      setPendingChanges({});
      clearError();

      const loadStatus = async () => {
        try {
          const result = await fetchProfileStatus(business.business_registry_number);
          setProfileFields(result.fields || {});
          setProfileExists(true);
        } catch (err) {
          console.error("Failed to fetch status:", err);
          if (err.message && (err.message.includes('not found') || err.message.includes('404'))) {
            setProfileExists(false);
            clearError();
          } else {
            setLocalError(err.message);
          }
        } finally {
          setInitialFetchDone(true);
        }
      };
      loadStatus();
    }
  }, [isOpen, business, fetchProfileStatus, clearError]);

  const handleCreateProfile = async () => {
    setLocalError(null);
    setSuccessMessage('');

    try {
      const result = await createDefaultProfile(business.business_registry_number);
      setProfileExists(true);
      setProfileFields(result.fields || {});
      setSuccessMessage('Perfil base generado exitosamente. Todos los campos están configurados como privados.');

      await createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'public_profile_create',
        change_reason: `Business ${business.business_registry_number} - Generated default public profile record`
      });

    } catch (err) {
      setLocalError(err.message);
    }
  };

  const handleToggleField = (fieldName) => {
    const currentValue = profileFields[fieldName] || false;
    const newValue = !currentValue;

    setPendingChanges(prev => ({
      ...prev,
      [fieldName]: newValue
    }));

    setProfileFields(prev => ({
      ...prev,
      [fieldName]: newValue
    }));
  };

  const handleSaveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      setLocalError('No hay cambios para guardar');
      return;
    }

    setLocalError(null);
    setSuccessMessage('');

    try {
      // Use bulk update for multiple changes
      await bulkUpdateProfileFields(business.business_registry_number, pendingChanges);

      setSuccessMessage(`${Object.keys(pendingChanges).length} campo(s) actualizado(s) exitosamente.`);

      // Track changes
      const changesList = Object.entries(pendingChanges).map(([field, value]) =>
        `${fieldConfig[field]?.label || field}: ${value ? 'PÚBLICO' : 'PRIVADO'}`
      );

      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'public_profile_bulk_toggle',
          change_reason: `Business ${business.business_registry_number} (${business.business_name}) - ${changesList.join('; ')}`
        });
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track changes:', trackerErr);
      }

      setPendingChanges({});

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      setLocalError(err.message);
      // Revert changes on error
      setProfileFields(prev => {
        const reverted = { ...prev };
        Object.keys(pendingChanges).forEach(key => {
          reverted[key] = !pendingChanges[key];
        });
        return reverted;
      });
      setPendingChanges({});
    }
  };

  const handleEnableAll = () => {
    const changes = {};
    Object.keys(fieldConfig).forEach(fieldName => {
      if (!profileFields[fieldName]) {
        changes[fieldName] = true;
      }
    });

    setPendingChanges(prev => ({ ...prev, ...changes }));
    setProfileFields(prev => ({ ...prev, ...changes }));
  };

  const handleDisableAll = () => {
    const changes = {};
    Object.keys(fieldConfig).forEach(fieldName => {
      if (profileFields[fieldName]) {
        changes[fieldName] = false;
      }
    });

    setPendingChanges(prev => ({ ...prev, ...changes }));
    setProfileFields(prev => ({ ...prev, ...changes }));
  };

  if (!isOpen || !business) return null;

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-blue-50 to-purple-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Configuración de Visibilidad Pública
          </h3>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="mb-6 text-center">
            <h4 className="font-semibold text-gray-900 text-lg">{business.business_name}</h4>
            <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
          </div>

          {!initialFetchDone ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
          ) : !profileExists ? (
            <div className="flex flex-col items-center justify-center p-6 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50">
              <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mb-3 text-gray-400">
                <AlertCircle className="h-8 w-8" />
              </div>
              <h5 className="text-gray-700 font-semibold mb-2">Perfil No Encontrado</h5>
              <p className="text-xs text-center text-gray-500 mb-6 max-w-[250px]">
                Este negocio aún no tiene un registro de perfil público en la base de datos.
              </p>
              <button
                onClick={handleCreateProfile}
                disabled={loading}
                className="w-full py-2.5 px-4 rounded-xl font-bold text-sm bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                Generar Perfil Básico
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Bulk Actions */}
              <div className="flex gap-2 pb-4 border-b border-gray-200">
                <button
                  onClick={handleEnableAll}
                  disabled={loading}
                  className="flex-1 py-2 px-4 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Hacer Todo Público
                </button>
                <button
                  onClick={handleDisableAll}
                  disabled={loading}
                  className="flex-1 py-2 px-4 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Hacer Todo Privado
                </button>
              </div>

              {/* Individual Field Toggles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(fieldConfig).map(([fieldName, config]) => {
                  const Icon = config.icon;
                  const isActive = profileFields[fieldName] || false;
                  const isPending = fieldName in pendingChanges;

                  return (
                    <div
                      key={fieldName}
                      className={`relative p-4 rounded-lg border-2 transition-all ${isPending ? 'border-yellow-400 bg-yellow-50' :
                        isActive ? `border-${config.color}-200 bg-${config.color}-50` :
                          'border-gray-200 bg-white'
                        }`}
                    >
                      {isPending && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-200 text-yellow-800">
                            Pendiente
                          </span>
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-5 w-5 ${isActive ? `text-${config.color}-600` : 'text-gray-400'}`} />
                          <span className="font-medium text-gray-800 text-sm">{config.label}</span>
                        </div>
                      </div>

                      {config.requiresImage && (
                        <p className="text-xs text-orange-600 mb-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Requiere imagen actual
                        </p>
                      )}

                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold uppercase ${isActive ? `text-${config.color}-600` : 'text-gray-400'}`}>
                          {isActive ? 'Público' : 'Privado'}
                        </span>
                        <button
                          onClick={() => handleToggleField(fieldName)}
                          disabled={loading}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? `bg-${config.color}-600` : 'bg-gray-300'
                            } disabled:opacity-50`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'
                              }`}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(localError || error) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm animate-fade-in">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">Error</p>
                <p className="text-red-700">{localError || error}</p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm animate-fade-in">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          )}
        </div>

        {/* Footer with Save/Cancel */}
        {profileExists && initialFetchDone && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50 font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={!hasPendingChanges || loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Guardar Cambios {hasPendingChanges && `(${Object.keys(pendingChanges).length})`}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// =================================================================
// 2. DeletionConfirmationDialog Component
// =================================================================
const DeletionConfirmationDialog = ({
  isOpen,
  onClose,
  business,
  onConfirmDelete,
  onConfirmForceDelete,
  onConfirmCascadeDelete,
  loading,
  error,
  successMessage,
  subscriptionWarning
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');
  const [deletionMode, setDeletionMode] = useState('regular');
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const [cascadeConfirmationText, setCascadeConfirmationText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConfirmationInput('');
      setCascadeConfirmationText('');
      setDeletionMode('regular');
      setShowSubscriptionWarning(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (subscriptionWarning && subscriptionWarning.details) {
      setShowSubscriptionWarning(true);
    }
  }, [subscriptionWarning]);

  if (!isOpen || !business) return null;

  const isConfirmationValid = confirmationInput === business.business_registry_number;
  const isCascadeConfirmationValid = cascadeConfirmationText === 'ELIMINAR TODO';

  const handleRegularDelete = () => {
    onConfirmDelete(confirmationInput, false);
  };

  const handleForceDelete = () => {
    if (onConfirmForceDelete) {
      onConfirmForceDelete(confirmationInput);
    } else {
      onConfirmDelete(confirmationInput, true);
    }
  };

  const handleCascadeDelete = () => {
    if (onConfirmCascadeDelete) {
      onConfirmCascadeDelete(confirmationInput);
    }
  };

  const switchToCascadeMode = () => {
    setDeletionMode('cascade');
    setShowSubscriptionWarning(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60]">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 relative">

          <div className="text-center">
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${deletionMode === 'cascade' ? 'bg-purple-100' : 'bg-red-100'
              }`}>
              {deletionMode === 'cascade' ? (
                <AlertOctagon className="h-8 w-8 text-purple-600" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-red-600" />
              )}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {deletionMode === 'cascade'
                ? '🔴 ELIMINACIÓN TOTAL - EXTREMA PRECAUCIÓN'
                : '¿Está absolutamente seguro?'}
            </h3>

            <p className="text-gray-600 text-sm">
              {deletionMode === 'cascade'
                ? 'Está a punto de ELIMINAR PERMANENTEMENTE TODO:'
                : 'Está a punto de eliminar permanentemente el negocio:'}
            </p>

            <p className="font-bold text-lg my-2 text-gray-800 break-words">
              {business.business_name}
            </p>
          </div>

          {showSubscriptionWarning && subscriptionWarning && (
            <div className="my-4 p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-2">
                    Este negocio tiene una suscripción activa
                  </h4>

                  {subscriptionWarning.details && (
                    <div className="text-sm text-orange-800 space-y-1">
                      <p><strong>ID de Suscripción:</strong> {subscriptionWarning.details.subscription_id}</p>
                      <p><strong>Tier:</strong> {subscriptionWarning.details.subscription_tier}</p>
                      <p><strong>Estado:</strong> {subscriptionWarning.details.subscription_status}</p>
                      <p><strong>Expira:</strong> {subscriptionWarning.details.current_period_end}</p>
                      {subscriptionWarning.details.cancel_at_period_end && (
                        <p className="text-orange-600">⚠️ Ya programada para cancelarse al final del periodo</p>
                      )}
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-white rounded border border-orange-200">
                    <p className="text-sm font-medium text-gray-900 mb-2">Opciones disponibles:</p>
                    <div className="space-y-2 text-sm">
                      <button
                        onClick={() => setDeletionMode('force')}
                        className="w-full text-left px-3 py-2 bg-orange-100 hover:bg-orange-200 rounded transition-colors"
                      >
                        <strong>1. Forzar Eliminación:</strong> Cancelará la suscripción en Stripe y eliminará el negocio
                      </button>
                      <button
                        onClick={switchToCascadeMode}
                        className="w-full text-left px-3 py-2 bg-purple-100 hover:bg-purple-200 rounded transition-colors"
                      >
                        <strong>2. Eliminación Total (Cascade):</strong> Opción nuclear - elimina TODOS los datos de suscripción
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!successMessage ? (
            <>
              <div className={`my-4 p-4 rounded-lg text-sm ${deletionMode === 'cascade'
                ? 'bg-purple-50 border-2 border-purple-300 text-purple-900'
                : 'bg-red-50 border border-red-200 text-red-900'
                }`}>
                {deletionMode === 'cascade' ? (
                  <div className="space-y-2">
                    <p className="font-bold text-base">⚠️ ADVERTENCIA CRÍTICA - ELIMINACIÓN TOTAL ⚠️</p>
                    <p className="font-semibold">Esta acción eliminará PERMANENTEMENTE:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Todos los datos de registro del negocio</li>
                      <li>TODAS las tablas de suscripciones (activas e inactivas)</li>
                      <li>Todos los datos de facturación de Stripe</li>
                      <li>Todo el historial de suscripciones</li>
                      <li>Todos los productos y servicios</li>
                      <li>Todos los empleados y horarios</li>
                      <li>Todos los códigos QR y datos de seguimiento</li>
                      <li>Todos los registros de transacciones</li>
                      <li>Todos los datos de verificación</li>
                      <li>Todo el perfil público del negocio</li>
                      <li><strong>MÁS DE 30 TABLAS DE LA BASE DE DATOS</strong></li>
                    </ul>
                    <p className="font-bold mt-3 text-purple-900">
                      🚨 Esta acción NO SE PUEDE DESHACER - NO HAY RECUPERACIÓN POSIBLE 🚨
                    </p>
                    <p className="mt-2 text-xs">
                      Las suscripciones activas en Stripe serán canceladas inmediatamente.
                    </p>
                  </div>
                ) : deletionMode === 'force' ? (
                  <div className="space-y-2">
                    <p className="font-bold">⚠️ Eliminación Forzada</p>
                    <p>Esta acción:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Cancelará la suscripción activa en Stripe</li>
                      <li>Eliminará todos los datos del negocio</li>
                      <li>Eliminará productos, empleados, y citas</li>
                    </ul>
                    <p className="font-semibold mt-2">Esta acción NO se puede deshacer.</p>
                  </div>
                ) : (
                  <>
                    Esta acción **no se puede deshacer**. Se borrarán todos los datos asociados,
                    incluyendo productos, empleados y citas.
                  </>
                )}
              </div>

              {!showSubscriptionWarning && (
                <div className="my-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Modo de eliminación:</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDeletionMode('regular')}
                      className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${deletionMode === 'regular'
                        ? 'bg-red-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      Regular
                    </button>
                    <button
                      onClick={() => setDeletionMode('force')}
                      className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${deletionMode === 'force'
                        ? 'bg-orange-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      Forzar
                    </button>
                    <button
                      onClick={() => setDeletionMode('cascade')}
                      className={`flex-1 px-3 py-2 text-xs rounded transition-colors ${deletionMode === 'cascade'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      Total (Cascade)
                    </button>
                  </div>
                </div>
              )}

              <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700 mt-6">
                Para confirmar, por favor escriba el número de registro: <br />
                <strong className="font-mono text-red-600 select-all">
                  {business.business_registry_number}
                </strong>
              </label>
              <input
                id="confirmation"
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
                placeholder="Escriba el número de registro"
              />

              {deletionMode === 'cascade' && isConfirmationValid && (
                <div className="mt-4">
                  <label htmlFor="cascade-confirmation" className="block text-sm font-medium text-purple-700 mb-2">
                    Confirmación adicional requerida. Escriba exactamente: <br />
                    <strong className="font-mono text-purple-600 select-all text-base">
                      ELIMINAR TODO
                    </strong>
                  </label>
                  <input
                    id="cascade-confirmation"
                    type="text"
                    value={cascadeConfirmationText}
                    onChange={(e) => setCascadeConfirmationText(e.target.value)}
                    className="w-full p-2 border-2 border-purple-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
                    autoComplete="off"
                    placeholder="ELIMINAR TODO"
                  />
                  {cascadeConfirmationText && !isCascadeConfirmationValid && (
                    <p className="mt-1 text-xs text-purple-600">
                      ⚠️ Debe escribir exactamente "ELIMINAR TODO"
                    </p>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>

                {deletionMode === 'cascade' ? (
                  <button
                    onClick={handleCascadeDelete}
                    disabled={!isConfirmationValid || !isCascadeConfirmationValid || loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-300 disabled:cursor-not-allowed font-semibold"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin h-5 w-5" />
                    ) : (
                      <>
                        <AlertOctagon className="h-5 w-5" />
                        ELIMINAR TODO
                      </>
                    )}
                  </button>
                ) : deletionMode === 'force' ? (
                  <button
                    onClick={handleForceDelete}
                    disabled={!isConfirmationValid || loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-orange-300 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin h-5 w-5" />
                    ) : (
                      <>
                        <Zap className="h-5 w-5" />
                        Forzar Eliminación
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleRegularDelete}
                    disabled={!isConfirmationValid || loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin h-5 w-5" />
                    ) : (
                      <>
                        <Trash2 className="h-5 w-5" />
                        Eliminar Negocio
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="mt-4 text-xs text-gray-500 text-center">
                {deletionMode === 'cascade' && (
                  <p className="text-purple-600 font-medium">
                    ⚠️ La eliminación total es irreversible y borrará más de 30 tablas
                  </p>
                )}
                {deletionMode === 'force' && (
                  <p className="text-orange-600 font-medium">
                    ⚠️ La eliminación forzada cancelará la suscripción activa en Stripe
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="mt-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="font-semibold text-green-800">Eliminación Exitosa</p>
              <p className="text-sm text-gray-600 mt-1">{successMessage}</p>
            </div>
          )}
        </div>

        {!loading && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};

// =================================================================
// 3. VerificationManagementDialog Component
// =================================================================
const VerificationManagementDialog = ({ isOpen, onClose, business, onVerificationUpdate }) => {
  const { updateBusinessVerification, loading: contextLoading, error: contextError } = useBusinessData();
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store initial values for comparison
  const [initialEmailVerified, setInitialEmailVerified] = useState(false);
  const [initialPhoneVerified, setInitialPhoneVerified] = useState(false);

  useEffect(() => {
    if (isOpen && business) {
      const emailState = business.verified_email || false;
      const phoneState = business.verified_phone || false;

      setEmailVerified(emailState);
      setPhoneVerified(phoneState);
      setInitialEmailVerified(emailState);
      setInitialPhoneVerified(phoneState);
      setHasChanges(false);
      setSuccessMessage('');
      setLocalError(null);
    }
  }, [isOpen, business]);

  const handleEmailToggle = () => {
    setEmailVerified(!emailVerified);
    setHasChanges(true);
    setSuccessMessage('');
    setLocalError(null);
  };

  const handlePhoneToggle = () => {
    setPhoneVerified(!phoneVerified);
    setHasChanges(true);
    setSuccessMessage('');
    setLocalError(null);
  };

  const handleSaveChanges = async () => {
    if (!business || !hasChanges) return;

    setIsSubmitting(true);
    setLocalError(null);

    try {
      await updateBusinessVerification(business.business_registry_number, {
        verified_email: emailVerified,
        verified_phone: phoneVerified
      });

      setSuccessMessage('Estado de verificación actualizado exitosamente');
      setHasChanges(false);

      // Track the verification changes in admin_tracker
      const changes = [];

      if (initialEmailVerified !== emailVerified) {
        changes.push(`Email verification: ${initialEmailVerified ? 'VERIFIED' : 'UNVERIFIED'} → ${emailVerified ? 'VERIFIED' : 'UNVERIFIED'}`);
      }

      if (initialPhoneVerified !== phoneVerified) {
        changes.push(`Phone verification: ${initialPhoneVerified ? 'VERIFIED' : 'UNVERIFIED'} → ${phoneVerified ? 'VERIFIED' : 'UNVERIFIED'}`);
      }

      if (changes.length > 0) {
        try {
          await createTracker({
            admin_id: adminId || 'unknown',
            change_type: 'verification_status_change',
            change_reason: `Business ${business.business_registry_number} (${business.business_name}) - ${changes.join('; ')}`
          });
          console.log('[AdminTracker] Verification change tracked successfully');
        } catch (trackerErr) {
          console.error('[AdminTracker] Failed to track verification change:', trackerErr);
        }
      }

      if (onVerificationUpdate) {
        onVerificationUpdate(business.business_registry_number, emailVerified, phoneVerified);
      }

      setTimeout(() => {
        onClose();
        setIsSubmitting(false);
      }, 1500);

    } catch (err) {
      console.error('Error updating verification:', err);
      setLocalError(err.message || 'Failed to update verification status');
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting || contextLoading;
  const displayError = localError || contextError;

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>

        <div className="p-4 sm:p-6 border-b border-gray-100 flex justify-between items-start">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            <span>Gestionar Verificación</span>
          </h3>
          <button onClick={onClose} disabled={isLoading} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-1">{business.business_name}</h4>
            <p className="text-sm text-gray-600 font-mono bg-gray-100 inline-block px-2 py-0.5 rounded">
              {business.business_registry_number}
            </p>
          </div>

          {successMessage ? (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center animate-fade-in">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-green-800 text-sm font-medium">{successMessage}</p>
            </div>
          ) : (
            <div className="space-y-6">

              <div className={`border rounded-lg p-4 transition-colors ${emailVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className={`h-5 w-5 ${emailVerified ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-gray-800">Correo Electrónico</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3 break-all pl-7">{business.business_email}</p>

                <div className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-100 shadow-sm">
                  <span className="text-sm text-gray-700">Estado:</span>
                  <div className="flex items-center space-x-3">
                    <span className={`text-xs font-bold uppercase tracking-wider ${emailVerified ? 'text-green-600' : 'text-gray-400'}`}>
                      {emailVerified ? 'Verificado' : 'No Verificado'}
                    </span>
                    <button
                      type="button"
                      onClick={handleEmailToggle}
                      disabled={isLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${emailVerified ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${emailVerified ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className={`border rounded-lg p-4 transition-colors ${phoneVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Phone className={`h-5 w-5 ${phoneVerified ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-gray-800">Número de Teléfono</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3 pl-7">{business.business_phone}</p>

                <div className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-100 shadow-sm">
                  <span className="text-sm text-gray-700">Estado:</span>
                  <div className="flex items-center space-x-3">
                    <span className={`text-xs font-bold uppercase tracking-wider ${phoneVerified ? 'text-green-600' : 'text-gray-400'}`}>
                      {phoneVerified ? 'Verificado' : 'No Verificado'}
                    </span>
                    <button
                      type="button"
                      onClick={handlePhoneToggle}
                      disabled={isLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${phoneVerified ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${phoneVerified ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {displayError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-red-800 text-sm">{displayError}</p>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={!hasChanges || isLoading || successMessage !== ''}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center font-medium shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Guardando...
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 4. EmailChangeDialog Component
// =================================================================
const EmailChangeDialog = ({ isOpen, onClose, business, onEmailUpdateSuccess }) => {
  const { initiateEmailChange, completeEmailChange, loading, error, clearError } = useEmailChange();
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [oldEmail, setOldEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [dialogError, setDialogError] = useState(null);
  const timerRef = useRef(null);
  const COOLDOWN_SECONDS = 60;

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setNewEmail(business.business_email || '');
      setVerificationCode('');
      setOldEmail(business.business_email);
      setCooldown(0);
      setDialogError(null);
      clearError();
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isOpen, business, clearError]);

  useEffect(() => {
    if (cooldown > 0 && isOpen) {
      timerRef.current = setInterval(() => setCooldown(prev => prev - 1), 1000);
    } else if (cooldown === 0 && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [cooldown, isOpen]);

  const handleInitiateChange = async () => {
    setDialogError(null);
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setDialogError('Por favor, ingrese un email válido.');
      return;
    }
    // Prevent sending code if the new email is the same as the old one
    if (newEmail.trim().toLowerCase() === oldEmail.trim().toLowerCase()) {
      setDialogError('El nuevo email debe ser diferente al actual.');
      return;
    }

    try {
      // Logic Update: We assume initiateEmailChange sends the code to the 'newEmail' argument passed here
      await initiateEmailChange(business.business_registry_number, newEmail);
      setCooldown(COOLDOWN_SECONDS);
      setStep(2);
    } catch (err) { setDialogError(err.message || 'Error al enviar código.'); }
  };

  const handleCompleteChange = async () => {
    setDialogError(null);
    if (verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
      setDialogError('El código debe ser de 6 dígitos.');
      return;
    }
    try {
      await completeEmailChange(business.business_registry_number, newEmail, verificationCode);

      // Track the email change in admin_tracker
      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'email_change',
          change_reason: `Business ${business.business_registry_number} (${business.business_name}) - Email changed from "${oldEmail}" to "${newEmail}" (Verified via new email)`
        });
        console.log('[AdminTracker] Email change tracked successfully');
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track email change:', trackerErr);
      }

      onEmailUpdateSuccess(business.business_registry_number, newEmail);
      onClose();
    } catch (err) { setDialogError(err.message || 'Error al completar el cambio.'); }
  };

  const handleClose = () => { if (!loading) onClose(); };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4" onClick={handleClose}>
      <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-100 flex justify-between items-start">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-orange-500" />
            <span>Actualizar Email</span>
          </h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600"><XCircle className="h-6 w-6" /></button>
        </div>
        <div className="p-4 sm:p-6">
          {step === 1 ? (
            <div>
              <p className="mb-4 text-sm text-gray-600">
                Paso 1: Ingrese el nuevo email que el negocio desea utilizar.
                <br />
                <span className="text-xs text-gray-500">Email actual: {oldEmail}</span>
                <br />
                <span className="font-medium text-orange-600">
                  Nota: El código de verificación se enviará al NUEVO email.
                </span>
              </p>
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                disabled={loading}
                placeholder="ejemplo@nuevo-email.com"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none"
              />
              <button
                onClick={handleInitiateChange}
                disabled={loading || cooldown > 0}
                className="w-full mt-4 p-2 bg-orange-500 text-white rounded disabled:bg-gray-400 font-medium"
              >
                {cooldown > 0 ? `Reenviar en ${cooldown}s` : `Enviar Código a ${newEmail ? 'Nuevo Email' : '...'}`}
              </button>
            </div>
          ) : (
            <div>
              <p className="mb-4 text-sm text-gray-600">
                Paso 2: Ingrese el código de 6 dígitos enviado al <strong className="text-green-600">NUEVO email</strong>:
                <br />
                <strong className="text-gray-800 break-all text-lg block mt-1 text-center bg-gray-50 p-2 rounded border border-gray-200">
                  {newEmail}
                </strong>
              </p>
              <input
                type="text"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                maxLength={6}
                placeholder="000000"
                disabled={loading}
                className="w-full p-2 text-center text-2xl tracking-[0.5em] font-bold border rounded focus:ring-2 focus:ring-green-500 outline-none"
              />
              <button
                onClick={handleCompleteChange}
                disabled={loading || verificationCode.length !== 6}
                className="w-full mt-4 p-2 bg-green-500 text-white rounded disabled:bg-gray-400 font-medium shadow-sm"
              >
                Verificar y Guardar Cambio
              </button>
              <button
                onClick={() => setStep(1)}
                disabled={loading}
                className="w-full mt-2 p-2 border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
              >
                Corregir Email
              </button>
            </div>
          )}
          {(dialogError || error) && (
            <div className="mt-4 p-2 bg-red-50 text-red-600 text-sm rounded border border-red-100 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {dialogError || error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 0c. CopyContactsDialog Component
// =================================================================
const CopyContactsDialog = ({ isOpen, onClose, searchTerm, filters }) => {
  const { getBusinesses, searchBusinesses } = useBusinessData();
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [contactType, setContactType] = useState('emails'); // 'emails' | 'phones' | 'both'
  const [format, setFormat] = useState('comma');             // 'comma' | 'newline' | 'semicolon'
  const [dedupe, setDedupe] = useState(true);

  // Fetch ALL matching businesses across all pages when the dialog opens
  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    setError(null);
    setBusinesses([]);

    const fetchAll = async () => {
      setLoading(true);
      try {
        const options = { limit: 10000, offset: 0, ...filters };
        const result = searchTerm?.trim()
          ? await searchBusinesses(searchTerm, options)
          : await getBusinesses(options);
        setBusinesses(result?.businesses || []);
      } catch (err) {
        setError(err.message || 'Error al cargar los negocios');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [isOpen, searchTerm, filters, getBusinesses, searchBusinesses]);

  // Build the output text based on current selections
  const { output, itemCount } = (() => {
    if (!businesses.length) return { output: '', itemCount: 0 };

    const separator = format === 'comma' ? ', ' : format === 'newline' ? '\n' : '; ';

    let items;
    if (contactType === 'emails') {
      items = businesses.map(b => b.business_email).filter(Boolean);
    } else if (contactType === 'phones') {
      items = businesses.map(b => b.business_phone).filter(Boolean);
    } else {
      items = businesses
        .filter(b => b.business_email || b.business_phone)
        .map(b => `${b.business_email || '—'} | ${b.business_phone || '—'}`);
    }

    if (dedupe) items = [...new Set(items)];
    return { output: items.join(separator), itemCount: items.length };
  })();

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);

      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'bulk_contact_copy',
          change_reason: `Copied ${itemCount} ${contactType} (format: ${format}, dedupe: ${dedupe}) from filtered results`
        });
      } catch (e) {
        console.error('Tracking failed', e);
      }

      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      setError('No se pudo copiar al portapapeles. Su navegador puede no soportar esta función.');
    }
  };

  if (!isOpen) return null;

  const previewText = output.length > 240 ? output.substring(0, 240) + '…' : output;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-orange-50 to-amber-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Copy className="h-5 w-5 text-orange-600" />
            Copiar Contactos
          </h3>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 text-orange-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-600">Cargando todos los negocios que coinciden con el filtro…</p>
            </div>
          ) : error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          ) : businesses.length === 0 ? (
            <div className="py-6 text-center text-gray-500 text-sm">
              No hay negocios que coincidan con el filtro o búsqueda actual.
            </div>
          ) : (
            <>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <span className="font-bold text-orange-700">{businesses.length}</span>
                <span className="text-orange-800"> negocio(s) coinciden con el filtro actual</span>
                <span className="text-orange-600 text-xs block mt-0.5">(de todas las páginas)</span>
              </div>

              {/* Contact Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">¿Qué deseas copiar?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'emails', label: 'Emails', icon: Mail },
                    { value: 'phones', label: 'Teléfonos', icon: Phone },
                    { value: 'both', label: 'Ambos', icon: User }
                  ].map(opt => {
                    const Icon = opt.icon;
                    const active = contactType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setContactType(opt.value)}
                        className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 transition-all ${active
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 hover:border-orange-300 text-gray-600'
                          }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="text-xs font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Formato</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'comma', label: 'Coma', preview: 'a, b' },
                    { value: 'newline', label: 'Línea', preview: 'a ⏎ b' },
                    { value: 'semicolon', label: 'Punto y coma', preview: 'a; b' }
                  ].map(opt => {
                    const active = format === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setFormat(opt.value)}
                        className={`p-2.5 rounded-lg border-2 transition-all text-left ${active ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                          }`}
                      >
                        <div className={`text-xs font-semibold ${active ? 'text-orange-700' : 'text-gray-700'}`}>
                          {opt.label}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{opt.preview}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dedupe */}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={dedupe}
                  onChange={(e) => setDedupe(e.target.checked)}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Eliminar duplicados
              </label>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vista previa <span className="text-gray-500 font-normal">({itemCount} elementos)</span>
                </label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-xs text-gray-700 max-h-28 overflow-y-auto break-all whitespace-pre-wrap">
                  {previewText || '(vacío)'}
                </div>
              </div>
            </>
          )}
        </div>

        {!loading && businesses.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleCopy}
              disabled={itemCount === 0}
              className={`flex-1 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-sm ${copied
                ? 'bg-green-600 text-white'
                : 'bg-orange-600 text-white hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                }`}
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  ¡Copiado!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar ({itemCount})
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// =================================================================
// 5. AdminDashboard Component (MERGED VERSION)
// =================================================================
function AdminDashboard() {
  const { loginType, adminId } = useAuth();
  const { createTracker } = useAdminTracker();
  const [showSMSVerificationDialog, setShowSMSVerificationDialog] = useState(false);
  const [showSocialsDialog, setShowSocialsDialog] = useState(false);
  const {
    deleteBusiness,
    forceDeleteBusiness,
    forceCascadeDeleteBusiness,
    loading: deletionLoading,
    error: deletionError,
    subscriptionWarning,
    clearError: clearDeletionError
  } = useBusinessDeletion();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showChangeLogDialog, setShowChangeLogDialog] = useState(false);
  const [showEmailChangeDialog, setShowEmailChangeDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  const [showPublicToggleDialog, setShowPublicToggleDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleSocialsClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowSocialsDialog(true); };
  const closeSocialsDialog = () => { setShowSocialsDialog(false); setSelectedBusiness(null); };

  const [showReDefaultDialog, setShowReDefaultDialog] = useState(false);

  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSuccess, setDeletionSuccess] = useState('');

  const [filters, setFilters] = useState({
    category: '', verifiedEmail: null, verifiedPhone: null,
    dateFrom: '', dateTo: '', sortBy: '', sortDir: ''
  });

  const {
    getBusinesses, getBusinessStats, searchBusinesses, exportBusinessData,
    loading: searchLoading, error: searchError, clearError: clearSearchError, formatBusinessData
  } = useBusinessData();

  const {
    getChangeLogsByRegistryNumber, getChangeLogStats, getChangeLogSummary,
    loading: changeLogsLoading, error: changeLogsError, clearError: clearChangeLogsError
  } = useBusinessChangeLogs();

  const itemsPerPage = 10;

  // MODIFIED: Helper to open Google search for a business name
  const openGoogleSearch = (businessName, e) => {
    e.stopPropagation();
    const url = `https://www.google.com/search?q=${encodeURIComponent(businessName)}`;
    window.open(url, '_blank');
  };

  const handleScheduleClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowScheduleDialog(true); };
  const closeScheduleDialog = () => { setShowScheduleDialog(false); setSelectedBusiness(null); };

  const handleLocationClick = (business, e) => {
    e.stopPropagation();
    setSelectedBusiness(business);
    setShowLocationDialog(true);
  };

  const closeLocationDialog = () => {
    setShowLocationDialog(false);
    setSelectedBusiness(null);
  };

  useEffect(() => { loadStats(); }, []);

const loadBusinesses = useCallback(async (page = 1, search = searchTerm) => {
    const hasActiveFilters = filters.category || filters.verifiedEmail !== null || filters.verifiedPhone !== null || filters.dateFrom || filters.dateTo;

    // "Looking for all the businesses" = no search term and no narrowing filters.
    // In that case, only expose the most recent 500 records.
    const isBrowsingAll = !search.trim() && !hasActiveFilters;

    try {
      clearSearchError(); setHasSearched(true);
      const options = { limit: itemsPerPage, offset: (page - 1) * itemsPerPage, ...filters };
      const result = search.trim() ? await searchBusinesses(search, options) : await getBusinesses(options);
      if (result) {
        const formattedBusinesses = result.businesses.map(b => ({ ...formatBusinessData(b), lastTimeModified: b.business_created_at || b.registration_date }));
        setBusinesses(formattedBusinesses);
        // Cap the navigable total to 500 when browsing all; searches/filters keep their real count.
        setTotalCount(isBrowsingAll ? Math.min(result.total_count, MAX_ALL_BUSINESSES) : result.total_count);
        setCurrentPage(page);
      }
    } catch (err) { console.error('Error loading businesses:', err); setBusinesses([]); setTotalCount(0); }
  }, [searchTerm, filters, getBusinesses, searchBusinesses, formatBusinessData, clearSearchError]);

  const handleSMSVerificationClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowSMSVerificationDialog(true); };
  const closeSMSVerificationDialog = () => { setShowSMSVerificationDialog(false); setSelectedBusiness(null); };
  const handlePhoneVerificationComplete = (registryNumber, verified) => {
    if (verified) {
      setBusinesses(prev => prev.map(b =>
        b.business_registry_number === registryNumber
          ? { ...b, verified_phone: true, verificationStatus: b.verified_email ? 'Fully Verified' : 'Partially Verified' }
          : b
      ));
    }
  };

  const loadStats = useCallback(async () => {
    try { setStats(await getBusinessStats()); }
    catch (err) { console.error('Error loading stats:', err); }
  }, [getBusinessStats]);

  useEffect(() => {
    const timeoutId = setTimeout(() => { loadBusinesses(1, searchTerm); }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadBusinesses]);

  const openDeleteModal = (business, e) => { e.stopPropagation(); setBusinessToDelete(business); setIsDeleteModalOpen(true); setDeletionSuccess(''); clearDeletionError(); };
  const closeDeleteModal = () => { setIsDeleteModalOpen(false); setTimeout(() => { setBusinessToDelete(null); }, 300); };

  const handleConfirmDelete = async (confirmationInput, force = false) => {
    if (!businessToDelete || confirmationInput !== businessToDelete.business_registry_number) return;
    try {
      const result = await deleteBusiness(businessToDelete.business_registry_number, force);
      setDeletionSuccess(result.message || 'Negocio eliminado exitosamente.');
      setBusinesses(prev => prev.filter(b => b.business_registry_number !== businessToDelete.business_registry_number));
      setTotalCount(prev => prev - 1);
      setTimeout(closeDeleteModal, 3000);
    } catch (err) { console.error("Deletion failed:", err); }
  };

  const handleConfirmForceDelete = async (confirmationInput) => {
    if (!businessToDelete || confirmationInput !== businessToDelete.business_registry_number) return;
    try {
      const result = await forceDeleteBusiness(businessToDelete.business_registry_number);
      setDeletionSuccess(result.message || 'Negocio y suscripción eliminados exitosamente.');
      setBusinesses(prev => prev.filter(b => b.business_registry_number !== businessToDelete.business_registry_number));
      setTotalCount(prev => prev - 1);
      setTimeout(closeDeleteModal, 3000);
    } catch (err) { console.error("Force deletion failed:", err); }
  };

  const handleConfirmCascadeDelete = async (confirmationInput) => {
    if (!businessToDelete || confirmationInput !== businessToDelete.business_registry_number) return;
    try {
      const result = await forceCascadeDeleteBusiness(businessToDelete.business_registry_number, true);
      setDeletionSuccess(result.message || 'Negocio y TODOS los datos asociados eliminados permanentemente.');
      setBusinesses(prev => prev.filter(b => b.business_registry_number !== businessToDelete.business_registry_number));
      setTotalCount(prev => prev - 1);
      setTimeout(closeDeleteModal, 4000);
    } catch (err) { console.error("Cascade deletion failed:", err); }
  };

  const handleFilterChange = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setCurrentPage(1); };
  const applyFilters = () => { setCurrentPage(1); loadBusinesses(1); setShowFilters(false); };
  const clearFilters = () => {
    setFilters({
      category: '', verifiedEmail: null, verifiedPhone: null,
      dateFrom: '', dateTo: '', sortBy: '', sortDir: ''
    }); setCurrentPage(1); setBusinesses([]); setTotalCount(0); setHasSearched(false);
  };
  const handlePageChange = (newPage) => loadBusinesses(newPage);

  const handleBusinessClick = (business) => { setSelectedBusiness(business); setShowChangeLogDialog(true); clearChangeLogsError(); };
  const closeChangeLogDialog = () => { setShowChangeLogDialog(false); setSelectedBusiness(null); clearChangeLogsError(); };

  const handleEmailChangeClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowEmailChangeDialog(true); };
  const closeEmailChangeDialog = () => { setShowEmailChangeDialog(false); setSelectedBusiness(null); };
  const handleEmailUpdateSuccess = (registryNumber, newEmail) => setBusinesses(prev => prev.map(b => b.business_registry_number === registryNumber ? { ...b, business_email: newEmail, verified_email: false } : b));

  const handleVerificationClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowVerificationDialog(true); };
  const closeVerificationDialog = () => { setShowVerificationDialog(false); setSelectedBusiness(null); };

  const handlePublicToggleClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowPublicToggleDialog(true); };
  const closePublicToggleDialog = () => { setShowPublicToggleDialog(false); setSelectedBusiness(null); };

  const handleMasterEditClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowEditDialog(true); };
  const closeMasterEditDialog = () => { setShowEditDialog(false); setSelectedBusiness(null); };
  const handleMasterUpdateSuccess = () => { loadBusinesses(currentPage); };

  const handleReDefaultClick = (business, e) => { e.stopPropagation(); setSelectedBusiness(business); setShowReDefaultDialog(true); };
  const closeReDefaultDialog = () => { setShowReDefaultDialog(false); setSelectedBusiness(null); };

  const handleVerificationUpdate = (registryNumber, emailVerified, phoneVerified) => {
    setBusinesses(prev => prev.map(b => b.business_registry_number === registryNumber ? { ...b, verified_email: emailVerified, verified_phone: phoneVerified, verificationStatus: (emailVerified && phoneVerified) ? 'Fully Verified' : 'Partially Verified' } : b));
  };
  const handleExport = async () => { };

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);
  const hasActiveSearch = searchTerm.trim() || Object.values(filters).some(v => v !== '' && v !== null);
  const loading = searchLoading;

  return (
    <BusinessLocationProvider>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="bg-white border-b border-orange-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="relative w-full"><div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Search className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" /></div><input type="text" placeholder="Buscar negocios..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />{loading && (<div className="absolute inset-y-0 right-0 pr-3 flex items-center"><Loader2 className="h-4 w-4 animate-spin text-orange-500" /></div>)}</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Filter className="h-4 w-4" /><span>Filtros</span></button>{hasActiveSearch && (<button onClick={() => loadBusinesses(currentPage)} disabled={loading} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Actualizar</span></button>)}
                <button
                  onClick={() => setShowCopyDialog(true)}
                  disabled={loading || businesses.length === 0}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  <Copy className="h-4 w-4" />
                  <span>Copiar Contactos</span>
                </button>
                <button onClick={handleExport} disabled={loading || businesses.length === 0} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                  <Download className="h-4 w-4" />
                  <span>Exportar</span>
                </button>
              </div>
            </div>
            {showFilters && (<div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
                <div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Ordenar por</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      className="w-full text-sm border rounded-md"
                    >
                      <option value="">Fecha de registro (defecto)</option>
                      <option value="business_category">Categoría</option>
                      <option value="business_name">Nombre del negocio</option>
                      <option value="business_owner">Dueño</option>
                      <option value="registration_date">Fecha de registro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Dirección</label>
                    <select
                      value={filters.sortDir}
                      onChange={(e) => handleFilterChange('sortDir', e.target.value)}
                      className="w-full text-sm border rounded-md"
                    >
                      <option value="">Descendente (defecto)</option>
                      <option value="asc">Ascendente (A-Z)</option>
                      <option value="desc">Descendente (Z-A)</option>
                    </select>
                  </div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Categoría
                  </label>
                  <input type="text" value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className="w-full text-sm border rounded-md" />
                </div>
                <div><label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email Verificado

                </label>
                  <select value={filters.verifiedEmail === null ? '' : filters.verifiedEmail.toString()} onChange={(e) => handleFilterChange('verifiedEmail', e.target.value === '' ? null : e.target.value === 'true')} className="w-full text-sm border rounded-md"><option value="">Todos</option>
                    <option value="true">Verificado</option>
                    <option value="false">No Verificado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Teléfono Verificado</label>
                  <select value={filters.verifiedPhone === null ? '' : filters.verifiedPhone.toString()} onChange={(e) => handleFilterChange('verifiedPhone', e.target.value === '' ? null : e.target.value === 'true')} className="w-full text-sm border rounded-md">
                    <option value="">Todos</option>
                    <option value="true">Verificado</option>
                    <option value="false">No Verificado</option></select></div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Fecha Desde</label>
                  <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} className="w-full text-sm border rounded-md" /></div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Fecha Hasta</label>
                  <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} className="w-full text-sm border rounded-md" /></div></div>
              <div className="mt-3 sm:mt-4 flex gap-2"><button onClick={applyFilters} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md">Aplicar Filtros</button>
                <button onClick={clearFilters} className="px-4 py-2 text-sm border rounded-md">Limpiar</button></div></div>)}
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-xl border border-orange-200 overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-orange-200 flex justify-between items-center"><h2 className="text-base sm:text-xl font-semibold text-gray-800">{hasActiveSearch ? `Resultados (${totalCount})` : 'Directorio de Negocios'}</h2>{totalCount > 0 && (<span className="text-xs sm:text-sm text-gray-600">Mostrando {startIndex}-{endIndex} de {totalCount}</span>)}</div>
            {businesses.length > 0 ? (
              <>
                <div className="hidden lg:block overflow-x-auto w-full">
                  <table className="w-full"><thead className="bg-gradient-to-r from-orange-50 to-orange-100">
                    <tr>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700">Negocio</th>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700">Dueño</th>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700">Teléfono</th>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700">Verificación</th>
                      <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold text-gray-700 min-w-[280px]">Acciones</th>
                    </tr>
                  </thead>
                    <tbody className="divide-y divide-orange-100">
                      {businesses.map((business, index) => (
                        <tr key={business.id} className="hover:bg-orange-50 transition-colors cursor-pointer" onClick={() => handleBusinessClick(business)}>
                          {/* MODIFIED: Business name becomes a clickable link that opens Google search */}
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm font-medium text-gray-900">
                            <span
                              onClick={(e) => openGoogleSearch(business.business_name, e)}
                              className="cursor-pointer text-orange-600 hover:underline hover:text-orange-800 transition-colors"
                            >
                              {business.business_name}
                            </span>
                          </td>
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm text-gray-700">{business.business_owner}</td>
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm"><a href={`mailto:${business.business_email}`} className="text-orange-600 hover:text-orange-800" onClick={e => e.stopPropagation()}>{business.business_email}</a></td>
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm">{business.business_phone}</td>
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm"><span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${business.verificationStatus === 'Fully Verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{business.verificationStatus}</span></td>
                          <td className="px-4 xl:px-6 py-3 xl:py-4 text-xs xl:text-sm">
                            <div className="flex items-center flex-wrap gap-1">
                              <button type="button" onClick={(e) => handleScheduleClick(business, e)} className="p-2 text-teal-600 hover:bg-teal-100 rounded-full" title="Horario del Negocio"><Clock className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); handleBusinessClick(business); }} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Ver Historial"><Eye className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleVerificationClick(business, e)} className="p-2 text-green-600 hover:bg-green-100 rounded-full" title="Gestionar Verificación"><Shield className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleEmailChangeClick(business, e)} className="p-2 text-orange-600 hover:bg-orange-100 rounded-full" title="Actualizar Email"><Edit2 className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleSMSVerificationClick(business, e)} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full" title="Verificar Teléfono SMS"><MessageSquare className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handlePublicToggleClick(business, e)} className="p-2 text-purple-600 hover:bg-purple-100 rounded-full" title="Visibilidad Pública"><Globe className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleReDefaultClick(business, e)} className="p-2 text-cyan-600 hover:bg-cyan-100 rounded-full" title="Resetear Evaluación"><ClipboardList className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleLocationClick(business, e)} className="p-2 text-pink-600 hover:bg-pink-100 rounded-full" title="Ver Ubicaciones"><MapPin className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleSocialsClick(business, e)} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full" title="Redes Sociales"><Link2 className="h-4 w-4" /></button>
                              <button type="button" onClick={(e) => handleMasterEditClick(business, e)} className="p-2 text-purple-600 hover:bg-purple-100 rounded-full" title="Editar Perfil / Contraseña"><Settings className="h-4 w-4" /></button>
                              {loginType === 'master' && (
                                <button type="button" onClick={(e) => openDeleteModal(business, e)} className="p-2 text-red-600 hover:bg-red-100 rounded-full" title="Eliminar Negocio"><Trash2 className="h-4 w-4" /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lg:hidden divide-y divide-orange-100">
                  {businesses.map((business, index) => (
                    <div key={business.id} className="p-4 hover:bg-orange-50" onClick={() => handleBusinessClick(business)}>
                      <div className="flex justify-between items-start mb-2">
                        {/* MODIFIED: Business name heading becomes a clickable link that opens Google search */}
                        <h3
                          className="font-semibold text-gray-900 cursor-pointer text-orange-600 hover:underline"
                          onClick={(e) => openGoogleSearch(business.business_name, e)}
                        >
                          {business.business_name}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${business.verificationStatus === 'Fully Verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{business.verificationStatus}</span>
                      </div>
                      <p className="text-sm text-gray-600">{business.business_owner}</p>
                      <div className="mt-2 text-sm space-y-1"><a href={`mailto:${business.business_email}`} className="text-orange-600 hover:text-orange-800 block" onClick={e => e.stopPropagation()}>{business.business_email}</a><p>{business.business_phone}</p></div>
                      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-center gap-2 flex-wrap">
                        <button type="button" onClick={(e) => handleScheduleClick(business, e)} className="p-2 text-teal-600 hover:bg-teal-100 rounded-full" title="Horario del Negocio"><Clock className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleBusinessClick(business); }} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Ver Historial"><Eye className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleVerificationClick(business, e)} className="p-2 text-green-600 hover:bg-green-100 rounded-full" title="Gestionar Verificación"><Shield className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleEmailChangeClick(business, e)} className="p-2 text-orange-600 hover:bg-orange-100 rounded-full" title="Actualizar Email"><Edit2 className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleSMSVerificationClick(business, e)} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full" title="Verificar Teléfono SMS"><MessageSquare className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handlePublicToggleClick(business, e)} className="p-2 text-purple-600 hover:bg-purple-100 rounded-full" title="Visibilidad Pública"><Globe className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleReDefaultClick(business, e)} className="p-2 text-cyan-600 hover:bg-cyan-100 rounded-full" title="Resetear Evaluación"><ClipboardList className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleLocationClick(business, e)} className="p-2 text-pink-600 hover:bg-pink-100 rounded-full" title="Ver Ubicaciones"><MapPin className="h-5 w-5" /></button>
                        <button type="button" onClick={(e) => handleSocialsClick(business, e)} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full" title="Redes Sociales"><Link2 className="h-4 w-4" /></button>
                        <button type="button" onClick={(e) => handleMasterEditClick(business, e)} className="p-2 text-purple-600 hover:bg-purple-100 rounded-full" title="Editar Perfil / Contraseña"><Settings className="h-5 w-5" /></button>
                        {loginType === 'master' && (
                          <>
                            <button type="button" onClick={(e) => openDeleteModal(business, e)} className="p-2 text-red-600 hover:bg-red-100 rounded-full" title="Eliminar Negocio"><Trash2 className="h-5 w-5" /></button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">{loading ? <div><Loader2 className="h-10 w-10 text-orange-500 mx-auto mb-4 animate-spin" /><h3 className="text-lg font-medium text-gray-700">Buscando...</h3></div> : hasSearched ? <div><Search className="h-12 w-12 text-orange-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-gray-700">No se encontraron negocios</h3></div> : <div><Search className="h-12 w-12 text-orange-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-gray-700">Buscar Negocios</h3></div>}</div>
            )}
            {totalPages > 1 && (<div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-orange-200 flex justify-between items-center"><div className="text-xs sm:text-sm text-gray-700">Página {currentPage} de {totalPages}</div><div className="flex space-x-2"><button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading} className="px-4 py-2 border rounded-md text-sm">Anterior</button><button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading} className="px-4 py-2 border rounded-md text-sm">Siguiente</button></div></div>)}
          </div>
        </main>

        {/* Dialogs (unchanged) */}
        <BusinessChangeLogDialog isOpen={showChangeLogDialog} onClose={closeChangeLogDialog} business={selectedBusiness} getChangeLogsByRegistryNumber={getChangeLogsByRegistryNumber} getChangeLogStats={getChangeLogStats} getChangeLogSummary={getChangeLogSummary} changeLogsLoading={changeLogsLoading} changeLogsError={changeLogsError} clearChangeLogsError={clearChangeLogsError} />

        {selectedBusiness && (
          <>
            <VerificationManagementDialog isOpen={showVerificationDialog} onClose={closeVerificationDialog} business={selectedBusiness} onVerificationUpdate={handleVerificationUpdate} />
            <EmailChangeDialog isOpen={showEmailChangeDialog} onClose={closeEmailChangeDialog} business={selectedBusiness} onEmailUpdateSuccess={handleEmailUpdateSuccess} />
            <PublicToggleDialog isOpen={showPublicToggleDialog} onClose={closePublicToggleDialog} business={selectedBusiness} />
            <SMSVerificationDialog isOpen={showSMSVerificationDialog} onClose={closeSMSVerificationDialog} business={selectedBusiness} onVerificationComplete={handlePhoneVerificationComplete} />
            <BusinessEditDialog isOpen={showEditDialog} onClose={closeMasterEditDialog} business={selectedBusiness} onUpdateSuccess={handleMasterUpdateSuccess} />
            <BusinessScheduleDialog isOpen={showScheduleDialog} onClose={closeScheduleDialog} business={selectedBusiness} />
            <ReDefaultEvaluationDialog isOpen={showReDefaultDialog} onClose={closeReDefaultDialog} business={selectedBusiness} />
            <BusinessLocationDialog isOpen={showLocationDialog} onClose={closeLocationDialog} business={selectedBusiness} />
            <BusinessSocialsDialog isOpen={showSocialsDialog} onClose={closeSocialsDialog} business={selectedBusiness} />
          </>
        )}

        <CopyContactsDialog isOpen={showCopyDialog} onClose={() => setShowCopyDialog(false)} searchTerm={searchTerm} filters={filters} />

        <DeletionConfirmationDialog
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          business={businessToDelete}
          onConfirmDelete={handleConfirmDelete}
          onConfirmForceDelete={handleConfirmForceDelete}
          onConfirmCascadeDelete={handleConfirmCascadeDelete}
          loading={deletionLoading}
          error={deletionError}
          subscriptionWarning={subscriptionWarning}
          successMessage={deletionSuccess}
        />
      </div>
    </BusinessLocationProvider>
  );
}

export default AdminDashboard;